import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import * as Dialog from '@radix-ui/react-dialog';
import CloseSVG from '@birdi/icons/close.svg';
import GlobeSVG from '@birdi/icons/sites-globe.svg';
import { closeButtonCSS, contentCSS, portalCSS } from '@birdi/modal-kit';
import {
  BirdiTheme,
  buttonBlue,
  buttonOutlineBlue,
  forms,
} from '@birdi/theme';
import { LocationSelect } from '@birdi/google-places-autocomplete';
import { uploadOne } from '@birdi/uppy-s3';
import { stroot } from '@birdi/js-sdk';
import { mapSitesAPI } from '@birdi/js-sdk/src/public/map-sites';
import type { MapSiteBoundarySource } from '@birdi/js-sdk/src/public/map-sites';
import type { Location } from '@birdi/types/location';
import { StaticMapPreview } from './StaticMapPreview';
import { SiteImageUpload } from './SiteImageUpload';
import { invalidateSites } from './queries';
import {
  modalSeparatorCSS,
  modalFooterCSS,
  modalHalfButtonCSS,
  modalInputCSS,
  selectTheme,
} from './styles';
import { kmlToBoundaryGeoJSON, readAPIErrorMessage } from './utils';

interface CreateSiteModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

const fieldCSS = css`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const titleRowCSS = css`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  svg {
    width: 1.2em;
    height: 1.2em;
  }
`;

const optionalCSS = (theme: BirdiTheme) => css`
  color: ${theme.mono50};
  font-weight: normal;
`;

const hintCSS = (theme: BirdiTheme) => css`
  font-size: 0.8rem;
  color: ${theme.mono50};
  font-weight: normal;
`;

const fieldsetCSS = css`
  border: none;
  padding: 0;
  margin: 1rem 0;
  legend {
    font-weight: bold;
    margin-bottom: 0.5rem;
    padding: 0;
  }
`;

// A boundary-source option row. Vertically centres the (enlarged) radio, and
// paints the selected row with the lightOcean fill + deepOcean border.
const optionLabelCSS = (theme: BirdiTheme, selected: boolean) => css`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  margin-bottom: 0.75rem;
  border: 1px solid ${selected ? theme.deepOcean : theme.mono30};
  background: ${selected ? theme.lightOcean : theme.mono0};
  border-radius: 8px;
  cursor: pointer;
  input[type='radio'] {
    width: 1.15rem;
    height: 1.15rem;
    margin: 0;
    flex-shrink: 0;
    accent-color: ${theme.deepOcean};
  }
`;

const recommendedCSS = (theme: BirdiTheme) => css`
  display: inline-block;
  margin-top: 0.15rem;
  color: ${theme.flora};
  font-size: 0.8rem;
`;

export const CreateSiteModal = ({ open, setOpen }: CreateSiteModalProps) => {
  const theme = useTheme();
  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [location, setLocation] = useState<Location | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [boundarySource, setBoundarySource] =
    useState<MapSiteBoundarySource>('location');
  const [boundaryFile, setBoundaryFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset the wizard whenever it is (re)opened
  useEffect(() => {
    if (open) {
      setStep(1);
      setName('');
      setLocation(null);
      setImageFile(null);
      setBoundarySource('location');
      setBoundaryFile(null);
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const submit = async () => {
    if (!location?.center) return;
    setSubmitting(true);
    setError(null);
    try {
      let s3ImageUrl: string | undefined;
      if (imageFile) {
        // Uppy expects a Blob (see fileToUppyOpts) — passing the raw File can
        // stall the upload. And uploadOne never settles if the upload fails
        // (it treats an empty successful[] as success), so race a timeout to
        // turn a stuck upload into a visible error instead of endless spinning.
        const upload = (await Promise.race([
          uploadOne({
            data: new Blob([imageFile], { type: imageFile.type }),
            type: imageFile.type,
            name: imageFile.name,
            public: true,
          }),
          new Promise((_resolve, rejectTimeout) => {
            window.setTimeout(
              () =>
                rejectTimeout(
                  new Error('Image upload timed out — please try again.'),
                ),
              45000,
            );
          }),
        ])) as { uploadURL: string };
        s3ImageUrl = upload.uploadURL;
      }

      let boundaryGeoJSON: Record<string, unknown> | undefined;
      if (boundarySource === 'upload') {
        if (!boundaryFile) {
          throw new Error('Please choose a boundary file');
        }
        const lower = boundaryFile.name.toLowerCase();
        if (lower.endsWith('.geojson') || lower.endsWith('.json')) {
          let parsed: unknown;
          try {
            parsed = JSON.parse(await boundaryFile.text());
          } catch {
            throw new Error('That file is not valid GeoJSON');
          }
          if (!parsed || typeof parsed !== 'object' || !('type' in parsed)) {
            throw new Error('That file is not valid GeoJSON');
          }
          boundaryGeoJSON = parsed as Record<string, unknown>;
        } else if (lower.endsWith('.kml')) {
          // The API only accepts GeoJSON — convert KML in the browser
          boundaryGeoJSON = kmlToBoundaryGeoJSON(await boundaryFile.text());
        } else {
          throw new Error(
            'Shapefile upload is coming soon — please upload a GeoJSON or KML file.',
          );
        }
      }

      await mapSitesAPI.createSite(stroot('xoh1ba'), {
        name: name.trim(),
        address: location.name,
        center: location.center,
        bbox: location.bbox ?? null,
        boundarySource,
        ...(boundaryGeoJSON && { boundaryGeoJSON }),
        ...(s3ImageUrl && { s3ImageUrl }),
      });
      invalidateSites();
      setOpen(false);
    } catch (err) {
      setError(readAPIErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const step1Valid = !!name.trim() && !!location?.center;

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <div css={portalCSS}>
          <Dialog.Content
            css={(theme) => css`
              ${contentCSS(theme)};
              box-shadow: 0 12px 32px rgba(9, 30, 66, 0.18);
            `}
          >
            <Dialog.Close css={closeButtonCSS}>
              <CloseSVG />
            </Dialog.Close>
            <Dialog.Title id="create-site-info" css={titleRowCSS}>
              <GlobeSVG />
              Create a site
            </Dialog.Title>
            <hr css={modalSeparatorCSS} />

            {step === 1 && (
              <div>
                <label css={fieldCSS}>
                  Site name
                  <input
                    type="text"
                    value={name}
                    placeholder="eg: Quarry location"
                    data-testid="create-site-name"
                    css={modalInputCSS}
                    onChange={(event) => setName(event.target.value)}
                  />
                </label>
                <label htmlFor="create-site-address" css={fieldCSS}>
                  Address or coordinates
                  <LocationSelect
                    inputId="create-site-address"
                    defaultValue={location?.name || ''}
                    placeholder="Search for an address or coordinates"
                    theme={selectTheme(theme)}
                    onLocationSelect={(geocode) =>
                      setLocation(geocode.location)
                    }
                  />
                </label>
                <div css={fieldCSS}>
                  <span>
                    Site image <span css={optionalCSS}>(optional)</span>
                  </span>
                  <span css={hintCSS}>
                    Upload a site image. If none is selected it will default to
                    the latest orthophoto. Format: JPEG or PNG 00px x 00px
                  </span>
                  <SiteImageUpload
                    imageFile={imageFile}
                    onSelect={setImageFile}
                  />
                </div>
                <footer css={modalFooterCSS}>
                  <button
                    type="button"
                    css={(theme) => css`
                      ${buttonOutlineBlue(theme)};
                      ${modalHalfButtonCSS};
                    `}
                    onClick={() => setOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    css={(theme) => css`
                      ${buttonBlue(theme)};
                      ${modalHalfButtonCSS};
                    `}
                    disabled={!step1Valid}
                    data-testid="create-site-next"
                    onClick={() => setStep(2)}
                  >
                    Next: boundary →
                  </button>
                </footer>
              </div>
            )}

            {step === 2 && location?.center && (
              <div>
                <p
                  css={css`
                    margin-top: 0;
                  `}
                >
                  Site name: <strong>{name}</strong>
                </p>
                <StaticMapPreview
                  center={location.center}
                  bbox={location.bbox ?? null}
                  label={location.name}
                />
                <hr css={modalSeparatorCSS} />
                <fieldset css={fieldsetCSS}>
                  <legend>How should we set the boundary?</legend>
                  <label
                    css={(theme) =>
                      optionLabelCSS(theme, boundarySource === 'location')
                    }
                  >
                    <input
                      type="radio"
                      name="boundary-source"
                      checked={boundarySource === 'location'}
                      data-testid="boundary-source-location"
                      onChange={() => setBoundarySource('location')}
                    />
                    <span>
                      <strong>Use location boundary (synced from map)</strong>
                      <br />
                      <span css={hintCSS}>
                        We infer a boundary from the address you entered.
                      </span>
                      <br />
                      <span css={recommendedCSS}>Recommended</span>
                    </span>
                  </label>
                  <label
                    css={(theme) =>
                      optionLabelCSS(theme, boundarySource === 'upload')
                    }
                  >
                    <input
                      type="radio"
                      name="boundary-source"
                      checked={boundarySource === 'upload'}
                      data-testid="boundary-source-upload"
                      onChange={() => setBoundarySource('upload')}
                    />
                    <span>
                      <strong>Upload a boundary file</strong>
                      <br />
                      <span css={hintCSS}>
                        Import a KML, GeoJSON or shapefile
                      </span>
                      {boundarySource === 'upload' && (
                        <input
                          type="file"
                          accept=".kml,.geojson,.json,.zip"
                          data-testid="boundary-file-input"
                          css={css`
                            display: block;
                            margin-top: 0.5rem;
                          `}
                          onChange={(event) =>
                            setBoundaryFile(event.target.files?.[0] ?? null)
                          }
                        />
                      )}
                    </span>
                  </label>
                </fieldset>
                {error && <div css={forms.errorMessage}>{error}</div>}
                <footer css={modalFooterCSS}>
                  <button
                    type="button"
                    css={(theme) => css`
                      ${buttonOutlineBlue(theme)};
                      ${modalHalfButtonCSS};
                    `}
                    onClick={() => setStep(1)}
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    css={(theme) => css`
                      ${buttonBlue(theme)};
                      ${modalHalfButtonCSS};
                    `}
                    disabled={
                      submitting ||
                      (boundarySource === 'upload' && !boundaryFile)
                    }
                    data-testid="create-site-submit"
                    onClick={submit}
                  >
                    {submitting ? 'Creating…' : 'Create site'}
                  </button>
                </footer>
              </div>
            )}
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
